const DEFAULT_FALLBACK_MESSAGE = 'Le service est temporairement indisponible';

const META_KEYS = new Set([
  'type',
  'status',
  'title',
  'instance',
  'code',
  'status_code',
]);

function isObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function isMeaningfulText(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function normalizeText(value: string): string {
  return value.replace(/\s+/g, ' ').trim();
}

function collectMessages(
  value: unknown,
  bucket: string[],
  visited: WeakSet<object>,
  path?: string
): void {
  if (value === null || value === undefined) {
    return;
  }

  if (isMeaningfulText(value)) {
    const normalized = normalizeText(value);
    if (path) {
      bucket.push(`${path}: ${normalized}`);
      return;
    }
    bucket.push(normalized);
    return;
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    const normalized = `${value}`;
    if (path) {
      bucket.push(`${path}: ${normalized}`);
      return;
    }
    bucket.push(normalized);
    return;
  }

  if (Array.isArray(value)) {
    value.forEach((entry) => collectMessages(entry, bucket, visited, path));
    return;
  }

  if (!isObject(value)) {
    return;
  }

  if (visited.has(value)) {
    return;
  }
  visited.add(value);

  const preferredKeys = ['message', 'detail', 'error', 'errors', 'non_field_errors'];
  for (const key of preferredKeys) {
    if (value[key] !== undefined) {
      collectMessages(value[key], bucket, visited, path);
    }
  }

  Object.entries(value).forEach(([key, nestedValue]) => {
    if (preferredKeys.includes(key) || META_KEYS.has(key)) {
      return;
    }
    const label = path ? `${path}.${key}` : key;
    collectMessages(nestedValue, bucket, visited, label);
  });
}

export function extractApiErrorMessage(
  error: unknown,
  fallbackMessage = DEFAULT_FALLBACK_MESSAGE
): string {
  const messages: string[] = [];
  const visited = new WeakSet<object>();

  if (isObject(error) && error['error'] !== undefined) {
    collectMessages(error['error'], messages, visited);
    if (messages.length === 0) {
      collectMessages(error, messages, visited);
    }
  } else {
    collectMessages(error, messages, visited);
  }

  const unique = Array.from(
    new Set(messages.map((message) => normalizeText(message)).filter(Boolean))
  );

  if (unique.length === 0) {
    return fallbackMessage;
  }

  if (unique.length === 1) {
    return unique[0];
  }

  return unique.slice(0, 3).join(' | ');
}

export function normalizeApiErrorPayload(
  payload: unknown,
  fallbackMessage = DEFAULT_FALLBACK_MESSAGE
): Record<string, unknown> {
  const message = extractApiErrorMessage(payload, fallbackMessage);

  if (isObject(payload)) {
    return {
      ...payload,
      message,
      error: payload['error'] ?? message,
      detail: payload['detail'] ?? message,
    };
  }

  return {
    message,
    error: message,
    detail: message,
  };
}
